import React, { useState, useEffect, useCallback } from "react";
import { Button, Modal, Search } from "carbon-components-react";
import { AddAlt16, ChevronDown16, ChevronUp16, Column16, SubtractAlt16 } from "@carbon/icons-react";

const styles = require("./styles.module.scss");

interface Props {
  initialOptionsList: string[];
  initialSelectedList: string[];

  onUpdate: (optionsList: string[], selectedList: string[]) => void;
}

const EditColumns: React.FC<Props> = ({ initialOptionsList, initialSelectedList, onUpdate }) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);

  // staged changes
  const [selectedList, setSelectedList] = useState(initialSelectedList);
  const [optionsList, setOptionsList] = useState(initialOptionsList);
  const [filteredOptions, setFilteredOptions] = useState(initialOptionsList);

  const [textSearch, setTextSearch] = useState("");
  useEffect(() => {
    const matches =
      textSearch.trim() === ""
        ? optionsList
        : optionsList.filter(o => o.toLowerCase().includes(textSearch.toLowerCase()));
    setFilteredOptions(matches);
  }, [textSearch, optionsList, selectedList]);

  const reset = useCallback(() => {
    setOptionsList(initialOptionsList);
    setSelectedList(initialSelectedList);
    setTextSearch("");
    setModalIsOpen(false);
  }, [initialOptionsList, initialSelectedList, onUpdate]);

  const removeSelectedItem = key => {
    const idx = selectedList.findIndex(li => li === key);
    const newOptionItem = selectedList[idx];
    const updatedSelectedList = [...selectedList.slice(0, idx), ...selectedList.slice(idx + 1)];

    setSelectedList(updatedSelectedList);
    setOptionsList([newOptionItem, ...optionsList]);
  };

  const addSelectedItem = key => {
    const idx = optionsList.findIndex(li => li === key);
    const newSelectItem = optionsList[idx];
    const updatedOptionsList = [...optionsList.slice(0, idx), ...optionsList.slice(idx + 1)];

    setOptionsList(updatedOptionsList);
    setSelectedList([...selectedList, newSelectItem]);
  };

  const moveItemUp = key => {
    const idx = selectedList.findIndex(li => li === key);
    if (idx === 0) {
      return;
    }

    const reorderedItem = selectedList.splice(idx, 1);
    const begHalf = selectedList.slice(0, idx - 1);
    const endHalf = selectedList.slice(idx - 1);
    setSelectedList([...begHalf, ...reorderedItem, ...endHalf]);
  };

  const moveItemDown = key => {
    const idx = selectedList.findIndex(o => o === key);
    if (idx === selectedList.length - 1) {
      return;
    }

    const reorderedItem = selectedList.splice(idx, 1);
    const begHalf = selectedList.slice(0, idx + 1);
    const endHalf = selectedList.slice(idx + 1);
    setSelectedList([...begHalf, ...reorderedItem, ...endHalf]);
  };

  const sortedFilteredOptions = filteredOptions.sort();

  return (
    <div className={styles.editColumn}>
      <Modal
        open={modalIsOpen}
        primaryButtonText="Update"
        secondaryButtonText="Cancel"
        modalHeading="Edit Columns"
        onRequestClose={reset}
        onRequestSubmit={() => {
          onUpdate(optionsList, selectedList);
          setModalIsOpen(false);
        }}
      >
        <div className={styles.columnListContainer}>
          <div className={styles.columnList}>
            <div className={styles.columnListHeader}>
              <p>Hidden columns</p>
              <Search
                labelText="Search"
                placeholder="Search"
                size="sm"
                value={textSearch}
                onChange={e => setTextSearch(e.target.value)}
              />
            </div>
            <ul>
              {sortedFilteredOptions.map(val => (
                <li key={`options-${val}`} className={styles.listItem}>
                  {val}
                  <span
                    className={styles.addItemButton}
                    onClick={() => addSelectedItem(val)}
                    onKeyPress={() => addSelectedItem(val)}
                    role="button"
                    tabIndex={0}
                  >
                    <AddAlt16 />
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.columnList}>
            <div className={styles.columnListHeader}>
              <p>Visible columns</p>
            </div>
            <ul>
              {selectedList.map(val => (
                <li key={`options-${val}`} className={styles.listItem}>
                  {val}
                  <div className={styles.selectedListActionWrapper}>
                    <span
                      className={styles.addItemButton}
                      onClick={() => moveItemUp(val)}
                      onKeyPress={() => moveItemUp(val)}
                      role="button"
                      tabIndex={0}
                    >
                      <ChevronUp16 />
                    </span>
                    <span
                      className={styles.addItemButton}
                      onClick={() => moveItemDown(val)}
                      onKeyPress={() => moveItemDown(val)}
                      role="button"
                      tabIndex={0}
                    >
                      <ChevronDown16 />
                    </span>
                    <span
                      className={styles.addItemButton}
                      onClick={() => removeSelectedItem(val)}
                      onKeyPress={() => removeSelectedItem(val)}
                      role="button"
                      tabIndex={0}
                    >
                      <SubtractAlt16 />
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Modal>
      <Button
        kind="ghost"
        onClick={() => setModalIsOpen(true)}
        renderIcon={Column16}
        iconDescription="Open Edit Columns"
      >
        Edit Columns
      </Button>
    </div>
  );
};

export default EditColumns;
